import styled, { css } from 'styled-components';
import { Reset } from 'ui/icons/action/Reset';
import { GradientFullCircle } from 'ui/icons/ai/GradientFullCircle';
import { scaleIn, fadeInScale, fadeIn } from 'modules/ai/copyAssist/components/styles';
import { duration, spacing, largeShadow, colors, caption, body2 } from 'ui/theme';
import { pxToRem } from 'utils/convert';

export const SharedFadeInAnimation = css`
  transform-origin: center center;
  animation-name: ${scaleIn};
  animation-duration: ${duration.long};
  animation-iteration-count: 1;
  animation-fill-mode: forwards;
`;

export const StyledCopyAssistDropdown = styled.div<{
  isShown?: boolean;
  top?: number;
}>`
  border-radius: ${spacing.half};
  background: white;
  z-index: 3;
  ${largeShadow};
  position: absolute;
  top: ${(props) => (props.top ? pxToRem(props.top + 8) : pxToRem(100))};
  width: 100%;
  transform-origin: top right;
  animation-name: ${scaleIn};
  animation-duration: ${duration.long};
  animation-iteration-count: 1;
  animation-fill-mode: forwards;
`;

export const StyledDropdownItem = styled.div<{
  isActive?: boolean;
  isLoading?: boolean;
}>`
  color: ${colors.textPrimary};
  display: flex;
  pointer-events: ${(props) => (props.isLoading ? 'none' : 'auto')};
  cursor: ${(props) => (props.isLoading ? 'default' : 'pointer')};
  min-height: ${pxToRem(44)};
  width: 100%;
  align-items: center;
  opacity: 0;
  animation-name: ${fadeInScale};
  animation-duration: ${duration.long};
  animation-iteration-count: 1;
  animation-fill-mode: forwards;
`;

export const TextSkeletonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.eighth};
  width: 100%;
`;

export const StyledDropdownItemWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: ${spacing.base};
  padding: ${spacing.half};
  &:hover {
    background-color: ${colors.backgroundSecondary};
  }
  border-radius: ${spacing.half};
`;

export const StyledGeneratedTextOption = styled.div`
  ${body2}
  animation-name: ${fadeIn};
  animation-duration: ${duration.long};
  animation-iteration-count: 1;
  animation-fill-mode: forwards;
  opacity: 0;
`;

export const StyledLoadingSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.half};
  color: ${colors.textSecondary};
`;

export const StyledLegalSection = styled.div`
  ${SharedFadeInAnimation}
  display: flex;
  align-items: center;
  gap: ${spacing.quarter};
  color: ${colors.textSecondary};
`;

export const StyledAnimatedResetBtn = styled(Reset)`
  transform: rotate(50deg);
  transition: all 300ms;
`;

export const StyledResetSection = styled.div`
  ${SharedFadeInAnimation}
  display: flex;
  align-items: center;
  gap: ${spacing.half};
  color: ${colors.purple100};
  &:hover ${StyledAnimatedResetBtn} {
    transform: rotate(-0.5deg);
  }
  cursor: pointer;
`;

export const StyledGradientSpinner = styled(GradientFullCircle)`
  circle {
    stroke-dasharray: 26;
    stroke-dashoffset: 21;
    transform-origin: center;
    animation: dash 3s ease-in-out infinite, rotate 1s linear infinite;
    /* stylelint-disable */
    color: url(#paint0_linear_846_22965);
  }

  @keyframes dash {
    0% {
      stroke-dashoffset: 51;
    }
    50% {
      stroke-dashoffset: 21;
    }
    100% {
      stroke-dashoffset: 51;
    }
  }

  @keyframes rotate {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

export const StyledCopyAssistDropdownFooter = styled.div`
  ${caption}
  background: ${colors.grey1};
  padding: ${spacing.base};
  border-radius: 0 0 ${spacing.half} ${spacing.half};
  margin-top: ${spacing.half};
  display: flex;
  justify-content: space-between;
  height: ${pxToRem(48)};
`;

export const StyledCopyAssistDropdownHeader = styled.div`
  ${caption};
  background: ${colors.grey1};
  padding: ${spacing.base};

  > span {
    color: ${colors.textSecondary}; 
  }
`;

export const StyledCircleIconBackground = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: ${colors.purple10};
  border-radius: 50%;
  min-width: ${pxToRem(28)};
  width: ${pxToRem(28)};
  height: ${pxToRem(28)};
  justify-content: center;
  cursor: pointer;

  &[data-bg-color='grey'] {
    border-color: ${colors.grey10};
    background-color: ${colors.grey10};
  }

  &[data-bg-color='green'] {
    border-color: ${colors.green10};
    background-color: ${colors.green10};
  }
`;
